import * as React from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import {
  ErrorMessage,
  PrimaryLoadingButton,
  PrimarySelect,
  PrimaryTextField,
  SecondaryButton,
} from 'components/molecules';
import * as yup from 'yup';
import { IOption } from 'interfaces';
import { getOptionsFromEnum } from 'utils/data-utils';
import {
  ISupport,
  ReferenceType,
  SupportFeedbackTypes,
} from 'pages/sessions/components/session-summary/interface';
import { useDispatch } from 'react-redux';
import { sessionSummaryActions } from 'pages/sessions/components/session-summary/slice';
import { yupResolver } from '@hookform/resolvers/yup';
import ClosableModal from '../closable-modal';
import SuccessOfFaildAlert from '../success-or-failed-alert';

const supportFormValidation = yup.object().shape({
  feedback: yup.string().trim().required().label('Comment'),
  feedbackType: yup.string().trim().required().label('Feedback Type'),
});

interface FeedbackFormProps {
  open: boolean;
  reference: string;
  referenceType: ReferenceType;
}

const FeedbackForm: React.FC<FeedbackFormProps> = ({ open, reference, referenceType }) => {
  const [loading, setLoading] = React.useState<boolean>(false);
  const feedbackTypes: IOption[] = getOptionsFromEnum(SupportFeedbackTypes);
  const dispatch = useDispatch();
  const [show, setShow] = React.useState<boolean>(open);

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<ISupport>({
    defaultValues: {
      feedback: '',
      feedbackType: '',
    },
    resolver: yupResolver(supportFormValidation),
    shouldFocusError: true,
  });

  const handleCancel = () => {
    reset();
    setShow(false);
  };

  React.useEffect(() => {
    setShow(open);
  }, [open]);

  const onSubmit: SubmitHandler<ISupport> = (data) => {
    setLoading(true);
    const feedbackData = {
      feedbackType: data.feedbackType,
      feedback: data.feedback,
      reference: reference ?? '',
      referenceType,
    };
    dispatch(sessionSummaryActions.createSupportFeedback(feedbackData));
    handleCancel();
    setTimeout(() => {
      setLoading(false);
    }, 2000);
  };

  const handleSubmitAlertModal = (type: string) => {};

  return (
    <>
      <ClosableModal
        open={show}
        onClose={handleCancel}
        // onClose={handleCancel}
        title="Feedback Form"
        subTitle=""
        modalClass="get-support-modal"
      >
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className="row">
            <div className="col-md-12">
              <div className="form-item">
                <Controller
                  control={control}
                  name="feedbackType"
                  render={({ field }) => (
                    <PrimarySelect
                      {...field}
                      label="Feedback type"
                      options={feedbackTypes}
                      placeholder="Select Feedback Type"
                    />
                  )}
                />
                <ErrorMessage>{errors?.feedbackType?.message}</ErrorMessage>
              </div>
            </div>
          </div>
          <div className="row">
            <div className="col-md-12">
              <div className="form-item">
                <Controller
                  control={control}
                  name="feedback"
                  render={({ field }) => (
                    <PrimaryTextField
                      {...field}
                      label="Your feedback"
                      multiline
                      rows={3}
                      inputProps={{ maxlength: 600 }}
                    />
                  )}
                />
                <ErrorMessage>{errors?.feedback?.message}</ErrorMessage>
              </div>
            </div>
          </div>
          <div className="row">
            <div className="col-12 col-md-6 mb-2 mb-md-0">
              <SecondaryButton fullWidth onClick={handleCancel}>
                Cancel
              </SecondaryButton>
            </div>
            <div className="col-12 col-md-6">
              <PrimaryLoadingButton loading={loading} fullWidth type="submit">
                Submit Feedback
              </PrimaryLoadingButton>
            </div>
          </div>
        </form>
      </ClosableModal>
      <SuccessOfFaildAlert onSubmit={handleSubmitAlertModal} />
    </>
  );
};

export default FeedbackForm;
